import { BaseType, Selection } from 'd3-selection';
import { useChartContext } from '../../ChartContext';
import {
  GEO_MAP_AREA_LABEL_CLASSNAME,
  GEO_MAP_DATA_KEY_ATTRIBUTE,
  GEO_MAP_GROUP_CLASSNAME,
  GEO_MAP_HEIGHT,
  GEO_MAP_PATH_CONTAINER,
  GEO_MAP_TOOLTIP_PREFIX,
  getContainerOffset,
  getKeyInGeoMapPropertiesByType,
  STATE_NEED_LABEL_IN_CB_DIVISION_MAP,
  STATE_NEED_LABEL_IN_CB_MAP,
} from '../utils';
import {
  ICountyMapProperties,
  IStateMapProperties,
} from '../../../../redux/slices/bootstrap';
import { useCallback, useEffect, useMemo } from 'react';
import {
  getGradientColor,
  getMapColorDictionary,
  gradientColorPattern,
} from '../colorUtils';
import { Geo, QueryType } from '../../../../utils/apollo/graphqlSchema';
import { assertCondition, assertNotNil } from '../../../../utils/types';
import * as d3 from 'd3';
import { geoPath } from 'd3-geo';
import {
  defaultNumberFormatOptions,
  formatValueWithMeasureFormat,
  globalFormat,
} from '../../../../utils/formatting';
import { useBootstrapSelector } from '../../../../redux/selectorHooks';
import { feature } from 'topojson';
import ReactDOM from 'react-dom';
import { IGeoDataDict, IGeoDataDictMeasure } from '../types';
import styled from 'styled-components';
import { theme } from '../../../../styles/theme';
import { floatingComponentStyles } from '../../../../styles/shareableStyles';

interface IProps {
  svgMapRef: React.MutableRefObject<
    Selection<SVGSVGElement, unknown, Element, any> | undefined
  >;
  isGeoMapReady: boolean;
  containerId: string;
}

const GeoMapTooltipContainer = styled.div`
  ${floatingComponentStyles};
  border-radius: 0;
`;

const GeoMapTooltipHeader = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${theme.colors.secondary900};
  padding: 5px 12px;
  border-bottom: 1px solid ${theme.colors.border};
`;

const GeoMapTooltipContent = styled.div`
  padding: 5px 12px;
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const GeoMapTooltipContentItem = styled.div<{ isActive: boolean }>`
  font-size: 12px;
  font-weight: ${({ isActive }) => (isActive ? 600 : 500)};
  color: ${({ isActive }) =>
    isActive ? theme.colors.mainBlue : theme.colors.secondary800};
`;

const ApplyColorAndTooltip = ({
  svgMapRef,
  isGeoMapReady,
  containerId,
}: IProps) => {
  const {
    metadata: { geo },
    chartData,
    displayedMeasures,
    settings: { chartSettings },
  } = useChartContext();
  assertCondition(chartData.queryType === QueryType.Expression);
  assertNotNil(geo);
  const { singleDayLabel, geoMapConfig } = chartSettings;
  const { isHeatmap, displayedMeasureKey } = geoMapConfig;
  const mapData = useBootstrapSelector('geoMapData');
  assertNotNil(mapData);
  const stateTopoData = mapData.stateMapData;

  const geoPropertyKey = useMemo(
    () => getKeyInGeoMapPropertiesByType(geo),
    [geo],
  );
  const colorDictionary = useMemo(() => getMapColorDictionary(geo), [geo]);

  const geoDataDict = useMemo(() => {
    const { columns, data } = chartData;
    const dayData = singleDayLabel
      ? data.find((d) => d.date === singleDayLabel)
      : data[0];
    assertNotNil(dayData);
    let largestValue: number | null = null;
    let geoDataDict: IGeoDataDict = {};

    // filter out the overall column
    const columnsWithoutOverall = columns.filter(
      (column) =>
        !chartData.measureControls.find((mc) => mc.key === column.key),
    );

    // find max value in dataColumns in selected measure
    // will use this largest value to calculate the color of the HEAT MAP of the selected day
    columnsWithoutOverall
      .filter((column) => column.measureKey === displayedMeasureKey)
      ?.forEach((column) => {
        if (
          largestValue === null ||
          (dayData[column.key] !== null &&
            Number(dayData[column.key]) > largestValue)
        ) {
          largestValue = Number(dayData[column.key]);
        }
      });
    // collect all measures from columns for each slice
    // so that we can display all measures on the tooltip when mouse hover on map area
    // step1: collect all slices that belong to the selected measure for map.
    columnsWithoutOverall

      .filter((column) => column.measureKey === displayedMeasureKey)
      .forEach((column) => {
        const value = Number(dayData[column.key]) ?? null;
        const measureInfo = displayedMeasures.find(
          (measure) => measure.key === column.measureKey,
        );
        assertNotNil(measureInfo);
        const measureNameWithLabel = measureInfo.label
          ? `${measureInfo.label}: ${measureInfo.name}`
          : measureInfo.name;
        geoDataDict[column.sliceName.toLowerCase()] = {
          value,
          heatColor: getGradientColor(value, largestValue),
          measures: [
            {
              name: measureNameWithLabel,
              value,
              key: column.key,
              isDisplayedOnMap: true,
              format: measureInfo.format,
            },
          ],
        };
      });
    // step2: collect all slices that not belong to the selected measure for map.
    columnsWithoutOverall
      .filter((column) => column.measureKey !== displayedMeasureKey)
      .forEach((column) => {
        const value = column.values[0] ?? null;
        const measureInfo = displayedMeasures.find(
          (measure) => measure.key === column.measureKey,
        );
        assertNotNil(measureInfo);
        const measureName = measureInfo.label
          ? `${measureInfo.label}: ${measureInfo.name}`
          : measureInfo.name;

        const sliceName = column.sliceName.toLowerCase();
        assertNotNil(geoDataDict[sliceName]?.measures);

        geoDataDict[sliceName].measures.push({
          name: measureName,
          value,
          key: column.key,
          isDisplayedOnMap: false,
          format: measureInfo.format,
        });
      });

    return geoDataDict;
  }, [displayedMeasures, displayedMeasureKey, chartData, singleDayLabel]);

  const getMapColor = useCallback(
    (fieldKey: string) => {
      if (isHeatmap) {
        return geoDataDict[String(fieldKey).toLowerCase()]?.heatColor ?? '#fff';
      } else {
        if (geo !== Geo.UsDma) {
          return colorDictionary[fieldKey];
        } else {
          // for us dma, the neighbor logic is not implemented yet, so we just return the color by dma code randomly.
          return colorDictionary[Number(fieldKey) % colorDictionary.length];
        }
      }
    },
    [isHeatmap, geoDataDict, geo, colorDictionary],
  );

  // add color to all areas, depends on if it is geo map or heat map
  useEffect(() => {
    if (svgMapRef.current && isGeoMapReady) {
      const fieldKeyInProperties = getKeyInGeoMapPropertiesByType(geo);
      const geoMapGroup = svgMapRef.current.selectAll(
        `.${GEO_MAP_GROUP_CLASSNAME}`,
      );

      // add color to all areas
      geoMapGroup.selectAll('path').attr('fill', function () {
        const { properties } = d3.select(this).datum() as {
          properties: ICountyMapProperties;
        };

        return getMapColor(properties[fieldKeyInProperties]);
      });

      // after all colors are applied, then set opacity to 1 to show the map
      svgMapRef.current.style('opacity', 1);
    }
  }, [svgMapRef, isGeoMapReady, getMapColor, geo, geoDataDict]);

  // add labels and numbers to all states
  useEffect(() => {
    // no need to add label on DMA map since there are too crowded
    if (geo === Geo.UsDma) {
      return;
    }
    if (svgMapRef.current && isGeoMapReady) {
      // remove existing labels, geoDict would be updated on displayedMeasure and measureKey change
      svgMapRef.current.selectAll(`.${GEO_MAP_AREA_LABEL_CLASSNAME}`).remove();
      const stateFeatures = feature(
        stateTopoData,
        stateTopoData.objects['us-state-map'],
      ).features;

      const projection = d3
        .geoAlbersUsa()
        .fitSize(
          [svgMapRef.current.node()?.clientWidth ?? 0, GEO_MAP_HEIGHT],
          feature(stateTopoData, stateTopoData.objects['us-state-map']),
        );
      const statePath = geoPath().projection(projection);

      svgMapRef.current
        .select(`.${GEO_MAP_PATH_CONTAINER}`)
        .append('g')
        .selectAll('g')
        .data(stateFeatures)
        .enter()
        .append('g')
        .attr('transform', (d) => `translate(${statePath.centroid(d)})`)
        .each(function (d) {
          const g = d3.select(this).style('pointer-events', 'none');
          const fontSize = geo === Geo.UsState ? '12px' : '16px';
          let isAddedLabel = false;

          // HI is island as special case, no need to check geoArea
          if (
            geo === Geo.UsState &&
            (d3.geoArea(d) > 0.0008 || d.properties.STATE_ABBR === 'HI')
          ) {
            isAddedLabel = true;
          } else if (geo === Geo.UsCensusRegion) {
            if (STATE_NEED_LABEL_IN_CB_MAP.includes(d.properties.STATE_ABBR)) {
              isAddedLabel = true;
            }
          } else if (geo === Geo.UsCensusDivision) {
            if (
              STATE_NEED_LABEL_IN_CB_DIVISION_MAP.includes(
                d.properties.STATE_ABBR,
              )
            ) {
              isAddedLabel = true;
            }
          }

          if (isAddedLabel) {
            // no labels for DMA map
            const fieldKeyInProperties = getKeyInGeoMapPropertiesByType(
              geo,
            ) as keyof IStateMapProperties;
            g.append('text')
              .attr('class', GEO_MAP_AREA_LABEL_CLASSNAME)
              .attr('text-anchor', 'middle')
              .attr('dominant-baseline', 'middle')
              .attr('font-size', fontSize)
              .text(d.properties[fieldKeyInProperties]);

            const dataKey = d.properties[fieldKeyInProperties].toLowerCase();
            const columnData = geoDataDict[dataKey];

            if (columnData?.value && columnData.value >= 0) {
              g.append('text')
                .attr('class', GEO_MAP_AREA_LABEL_CLASSNAME)
                .attr('text-anchor', 'middle')
                .attr('font-size', fontSize)
                .attr('dominant-baseline', 'middle')
                .attr('dy', '1em')
                .text(
                  globalFormat.formatNumber(
                    columnData.value,
                    defaultNumberFormatOptions.compact,
                  ),
                );
            }
          }
        });
    }
  }, [
    stateTopoData,
    svgMapRef,
    geoPropertyKey,
    geoDataDict,
    isGeoMapReady,
    geo,
  ]);

  // show tooltip on hover
  useEffect(() => {
    if (svgMapRef.current && isGeoMapReady) {
      const geoMapGroup = svgMapRef.current.selectAll(
        `.${GEO_MAP_GROUP_CLASSNAME}`,
      );

      const tooltip: Selection<SVGGElement, unknown, BaseType, unknown> = d3
        .select('#' + containerId)
        .selectAll(`.${GEO_MAP_TOOLTIP_PREFIX}${containerId}`);

      geoMapGroup
        .on('mouseover', function () {
          let dataKey = '';
          d3.select(this)
            .selectAll('path')
            .transition()
            .duration(100)
            .attr('fill', function () {
              // get fieldKey from data-field-key attribute
              dataKey = d3.select(this).attr(GEO_MAP_DATA_KEY_ATTRIBUTE);
              const mapColor: string = getMapColor(dataKey);
              if (isHeatmap) {
                if (
                  gradientColorPattern.indexOf(mapColor) >
                  gradientColorPattern.length / 2
                ) {
                  return d3.rgb(mapColor).darker(1).toString();
                } else {
                  return d3.rgb(mapColor).brighter(1).toString();
                }
              } else {
                return d3.color(mapColor)?.brighter(1).toString() || mapColor;
              }
            });
          // show tooltip
          tooltip.transition().duration(200).style('opacity', 1);

          // render tooltip content
          tooltip.html(() => {
            const columnData = geoDataDict[dataKey.toLowerCase()];
            const TooltipContent = () => (
              <GeoMapTooltipContainer>
                <GeoMapTooltipHeader>{dataKey}</GeoMapTooltipHeader>
                <GeoMapTooltipContent>
                  {columnData?.measures.map((measure: IGeoDataDictMeasure) => (
                    <GeoMapTooltipContentItem
                      key={measure.name}
                      isActive={measure.isDisplayedOnMap}
                    >
                      {measure.name}:{' '}
                      {measure?.value !== null && measure?.value >= 0
                        ? formatValueWithMeasureFormat(
                            measure.value,
                            measure.format,
                          )
                        : 'N/A'}
                    </GeoMapTooltipContentItem>
                  ))}
                </GeoMapTooltipContent>
              </GeoMapTooltipContainer>
            );

            const tooltipContainer = document.createElement('div');
            ReactDOM.render(<TooltipContent />, tooltipContainer);
            return tooltipContainer.innerHTML;
          });
        })
        .on('mousemove', function (event) {
          // update tooltip position when mouse move
          const tooltipWidth =
            tooltip?.node()?.getBoundingClientRect().width ?? 0;
          const containerElement = d3.select('#' + containerId).node();
          const containerWidth =
            containerElement instanceof Element
              ? containerElement.getBoundingClientRect().width
              : 0;
          const containerOffset = getContainerOffset(containerId);
          const mouseX = event.pageX - containerOffset.x;

          let leftPosition;
          if (mouseX + tooltipWidth + 20 > containerWidth) {
            // Open tooltip on the left side if it would exceed the right border
            leftPosition = mouseX - tooltipWidth - 10;
          } else {
            // Otherwise, open tooltip on the right side
            leftPosition = mouseX + 10;
          }

          tooltip
            .style('left', leftPosition + 'px')
            .style('top', event.pageY - containerOffset.y + 10 + 'px');
        })
        .on('mouseout', function () {
          // hide tooltip when mouse out and reset the color of the area
          d3.select(this)
            .selectAll('path')
            .transition()
            .duration(200)
            .attr('fill', function () {
              const dataKey = d3.select(this).attr(GEO_MAP_DATA_KEY_ATTRIBUTE);
              const mapColor: string = getMapColor(dataKey);
              return mapColor;
            });
          tooltip.transition().duration(200).style('opacity', 0);
        });
    }
  }, [
    isGeoMapReady,
    svgMapRef,
    isHeatmap,
    getMapColor,
    containerId,
    geoDataDict,
  ]);

  return <></>;
};

export default ApplyColorAndTooltip;
